/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import { IconDownload, IconPlus, IconSearch, IconX } from "@tabler/icons-react";
import GridView from "../../../components/common/GridView";
import NormalSearch from "../../../components/common/NormalSearch";
import CommonModal from "../../../components/common/CommonModal";
import AddClientCreateRequestModal from "./AddMerchantCreateRequestModal";
import AddClientRequestModal from "./AddMerchantRequestModal";
import {
  useMerchantAcceptLinkMutation,
  useMerchantAllListData,
  useMerchantRequestAcceptMutation,
  useMerchantRequestListData,
  useCustomerListData,
} from "../../../apis/merchantApis";
import useDebounce from "../../../utilities/useDebounce";
import Pagination from "../../../components/common/Pagination";
import formatDate from "../../../utilities/helper";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import EntriesSelector from "../../../components/common/EntriesSelector";
import { utils, writeFile } from "xlsx";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { useNavigate } from "react-router-dom";
import NewSelectInput from "../../../components/NewSelectInput";
import ToggleButton from "../../../components/ToggleButton";

type FormValues = {
  id?: string;
  name: string;
  mobileNumber: string;
  email: string;
  warehouseId: string;
  city: string;
  shippingCost: string;
  address: string;
  clientType: string;
};

type FilterType = "ALL" | "REQUESTS" | "CUSTOMERS";

export default function ClientsManagement({
  setHeaderActions,
  setHeaderButtons,
}: {
  setHeaderActions?: (node: React.ReactNode) => void;
  setHeaderButtons?: (node: React.ReactNode) => void;
}) {
  const [filter, setFilter] = useState<FilterType>("ALL");
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [showSearch, setShowSearch] = useState(false);

  const debouncedSearchFor = useDebounce(searchFor, 550);

  // Client Requests Hooks
  const {
    data: requestData,
    isPending: requestPending,
    isFetching: requestFetching,
    refetch: refetchRequests,
  } = useMerchantRequestListData(pageNo, limit, debouncedSearchFor);

  const { data: clients } = useMerchantAllListData();
  const [clickId, setClickId] = useState("");
  const [client, setClient] = useState("");
  const [clientError, setClientError] = useState("");

  // Mutations moved to sidebars

  // Customer Hooks
  const { data: customerData, isFetching: isCustomerFetching } =
    useCustomerListData(pageNo, limit, debouncedSearchFor);

  const navigate = useNavigate();

  // Calculate request/customer statistics dynamically
  const requestStats = {
    total: (requestData?.total || 0) + (customerData?.total || 0),
    requests: requestData?.total || 0,
    customers: customerData?.total || 0,
  };

  useEffect(() => {
    refetchRequests();
  }, [debouncedSearchFor, pageNo, limit]);

  // Reset page when filter changes
  useEffect(() => {
    setPageNo(1);
  }, [filter]);

  useEffect(() => {
    const handleRefetch = () => {
      refetchRequests();
    };
    window.addEventListener("refetchRequests", handleRefetch);
    return () => {
      window.removeEventListener("refetchRequests", handleRefetch);
    };
  }, []);

  // Handlers and form hooks moved to sidebars

  const { allowed: canCreateClientRequest } = usePermission(
    "client_request",
    "create",
  );
  const { allowed: canUpdateClientRequest } = usePermission(
    "client_request",
    "update",
  );

  // Modals replaced by sidebars

  // Helper: Merged List
  const getFilteredList = () => {
    const reqs = (requestData?.merchants || []).map((item: any) => ({
      ...item,
      isRequest: true,
    }));
    const custs = (customerData?.customers || []).map((item: any) => ({
      ...item,
      isCustomer: true,
    }));

    if (filter === "REQUESTS") {
      return reqs;
    }
    if (filter === "CUSTOMERS") {
      return custs;
    }

    // Sort combined by createdAt descending
    const merged = [...reqs, ...custs];
    merged.sort(
      (a, b) =>
        new Date(b.createdAt || 0).getTime() -
        new Date(a.createdAt || 0).getTime(),
    );
    return merged;
  };

  const getTotalPages = () => {
    if (filter === "REQUESTS") {
      return requestData?.totalPages || 0;
    }
    if (filter === "CUSTOMERS") {
      return customerData?.totalPages || 0;
    }
    return Math.max(
      requestData?.totalPages || 0,
      customerData?.totalPages || 0,
    );
  };

  const isDataLoading =
    filter === "REQUESTS"
      ? requestPending || requestFetching
      : filter === "CUSTOMERS"
        ? isCustomerFetching
        : requestPending || requestFetching || isCustomerFetching;

  const mapDataToExport = (item: any, index: number) => {
    if (item.isRequest) {
      return {
        "S/N": index + 1,
        Type: "Request",
        Name: item.name || "",
        Email: item.email || "",
        Platform: item.platformName || "",
        Status: item.status || "",
        Date: formatDate(item.createdAt),
      };
    } else {
      return {
        "S/N": index + 1,
        Type: "Customer",
        Name: item.name || "",
        Email: item.email || "",
        Location: [item.districtName, item.cityName, item.countryName]
          .filter(Boolean)
          .join(", "),
        Pincode: item.pincode || "",
        Status: item.status || "",
        Date: formatDate(item.createdAt),
      };
    }
  };

  const handleExport = () => {
    const listToExport = getFilteredList();
    if (listToExport.length === 0) {
      toast.error("No data to export");
      return;
    }
    const exportData = listToExport.map((item: any, index: number) =>
      mapDataToExport(item, index),
    );
    const worksheet = utils.json_to_sheet(exportData);
    worksheet["!cols"] = Object.keys(exportData[0] || {}).map(() => ({
      wch: 20,
    }));
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Requests");
    writeFile(workbook, `requests_${filter.toLowerCase()}_export.xlsx`);
    toast.success("Data exported successfully");
  };

  const filteredList = getFilteredList();
  const totalPages = getTotalPages();

  useEffect(() => {
    if (!setHeaderButtons) return;

    setHeaderButtons(
      <div className="flex items-center gap-2 bg-white/70 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-slate-200 shadow-sm text-[11px] select-none font-bold min-h-8">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Total:</span>
          <span className="text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
            {requestStats.total}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Requests:</span>
          <span className="text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
            {requestStats.requests}
          </span>
        </div>
        <span className="h-3 w-px bg-slate-300" />
        <div className="flex items-center gap-1.5">
          <span className="text-slate-500">Customers:</span>
          <span className="text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded">
            {requestStats.customers}
          </span>
        </div>
      </div>,
    );

    return () => {
      setHeaderButtons(null);
    };
  }, [
    setHeaderButtons,
    requestStats.total,
    requestStats.requests,
    requestStats.customers,
  ]);

  return (
    <>
      <div className="">
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Left side: Brand badge and filter dropdown */}
          <div className="flex flex-wrap items-center gap-3"></div>

          {/* Right side: Search + Export + Entries + Stats capsule */}
          <div className="flex items-center flex-wrap gap-2.5">
            <NewSelectInput
              name="requests-filter"
              placeholder="Select"
              value={filter}
              setValue={(val) => {
                setFilter((val as FilterType) || "ALL");
              }}
              selectOptions={[
                { id: "ALL", value: "ALL", text: "All" },
                { id: "REQUESTS", value: "REQUESTS", text: "Requests" },
                { id: "CUSTOMERS", value: "CUSTOMERS", text: "Customers" },
              ]}
              className="w-[100px]"
              removeDiv
              compact
            />
            {/* Sliding Search */}
            <div className="relative flex items-center">
              <div
                onClick={() => {
                  if (!showSearch) setShowSearch(true);
                }}
                className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-md bg-white shadow-sm h-8 ${
                  showSearch
                    ? "w-64 px-2 cursor-default"
                    : "w-8 justify-center cursor-pointer"
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowSearch(!showSearch);
                  }}
                  className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  title="Search"
                >
                  <IconSearch size={17} stroke={2.5} />
                </button>
                {showSearch && (
                  <>
                    <input
                      type="text"
                      value={searchFor}
                      onChange={(e) => setSearchFor(e.target.value)}
                      placeholder="Search..."
                      autoFocus
                      className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none focus:outline-none focus:ring-0"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchFor("");
                        setShowSearch(false);
                      }}
                      className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                      title="Clear & Close"
                    >
                      <IconX size={14} stroke={2.5} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Export to Excel */}
            <button
              onClick={handleExport}
              className="flex items-center justify-center w-8 h-8 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-md transition shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
              title="Export to Excel"
            >
              <IconDownload size={17} stroke={2.5} />
            </button>

            {totalPages * limit > 10 && (
              <EntriesSelector
                value={limit}
                options={[
                  10,
                  ...(totalPages * limit > 10 ? [20] : []),
                  ...(totalPages * limit > 20 ? [30] : []),
                  ...(totalPages * limit > 50 ? [50] : []),
                  ...(totalPages * limit > 100 ? [100] : []),
                ]}
                onChange={(val) => {
                  setLimit(val);
                  setPageNo(1);
                }}
              />
            )}

            {/* Stats capsule removed — now in page header */}

            {/* Add Request button */}
            {canCreateClientRequest && (
              <button
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent("openAddMerchantLink", {
                      detail: { id: "" },
                    })
                  );
                }}
                className="flex items-center justify-center p-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 duration-150"
                title="Add Request"
              >
                <IconPlus size={17} stroke={2.5} />
              </button>
            )}
          </div>
        </div>

        <div className="py-4">
          {isDataLoading ? (
            <TableSkeleton
              columns={[
                "Name",
                "Type",
                "Email",
                "Date / Location",
                "Status",
                "Actions",
              ]}
              rows={6}
            />
          ) : filteredList && filteredList.length > 0 ? (
            <GridView
              columns={3}
              className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
              data={filteredList}
              renderItem={(c: any) => {
                if (c.isRequest) {
                  // Render Client Request Card
                  const isAccepted = c.status === "ACCEPTED";
                  const statusText =
                    c.status?.slice(0, 1)?.toUpperCase() +
                    c?.status?.slice(1)?.toLowerCase();

                  return (
                    <div className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                      {/* Header */}
                      <div className="flex items-start justify-between border-b border-[#D2D2D2]/70 pb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-[11px] text-[#212121] uppercase tracking-wider font-semibold">
                              REQ-101
                            </p>
                            <span className="bg-blue-500/10 text-blue-600 border border-blue-500/20 text-[10px] font-medium px-2 py-0.5 rounded-full">
                              Merchant
                            </span>
                          </div>

                          <h3 className="text-[#000000] font-semibold text-lg mt-1 line-clamp-1">
                            {c.name || "-"}
                          </h3>
                        </div>

                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            isAccepted
                              ? "bg-green-500/15 text-green-600"
                              : "bg-yellow-500/15 text-yellow-600"
                          }`}
                        >
                          {statusText || "Pending"}
                        </span>
                      </div>

                      <div className="space-y-3">
                        {/* Platform */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">
                            Platform:
                          </span>
                          <span className="text-[#212121] text-sm font-medium">
                            {c.platformName || "-"}
                          </span>
                        </div>

                        {/* Email */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">Email:</span>
                          <span
                            className="text-[#212121] text-sm font-medium truncate max-w-[180px]"
                            title={c.email}
                          >
                            {c.email || "-"}
                          </span>
                        </div>

                        {/* Date */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">Date:</span>
                          <span className="text-[#212121] text-sm font-medium">
                            {formatDate(c.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                        {isAccepted ? (
                          <button
                            disabled
                            className="flex-1 h-10 rounded-lg bg-gray-100 text-gray-400 text-sm font-medium cursor-not-allowed"
                          >
                            Created
                          </button>
                        ) : canUpdateClientRequest ? (
                          <div className="flex gap-2 w-full">
                            <button
                              onClick={() => {
                                window.dispatchEvent(
                                  new CustomEvent("openAddMerchantLink", {
                                    detail: { id: c?.id },
                                  })
                                );
                              }}
                              className="flex-1 h-10 rounded-lg bg-[#FBD037]/20 hover:bg-[#FBD037]/40 text-[#000000] text-xs font-medium transition-colors"
                            >
                              Accept & Link
                            </button>
                            <button
                              onClick={() => {
                                window.dispatchEvent(
                                  new CustomEvent("openAddMerchantCreate", {
                                    detail: { request: c },
                                  })
                                );
                              }}
                              className="flex-1 h-10 rounded-lg bg-[#FBD037]/20 hover:bg-[#FBD037]/40 text-[#000000] text-xs font-medium transition-colors"
                            >
                              Accept & Create
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 h-10 rounded-lg bg-gray-100 text-gray-400 text-sm font-medium flex items-center justify-center cursor-not-allowed">
                            Pending
                          </div>
                        )}
                      </div>
                    </div>
                  );
                } else {
                  // Render Customer Card
                  const locationParts = [
                    c.districtName,
                    c.cityName,
                    c.countryName,
                  ].filter(Boolean);
                  const locationText = locationParts.join(", ") || "-";

                  return (
                    <div className="group relative rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-3">
                      <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

                      {/* Header */}
                      <div className="flex items-start justify-between border-b border-[#D2D2D2]/70 pb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-[11px] text-[#212121] uppercase tracking-wider font-semibold">
                              {c.customId || "CUST-101"}
                            </p>
                            <span className="bg-purple-500/10 text-purple-600 border border-purple-500/20 text-[10px] font-medium px-2 py-0.5 rounded-full">
                              Customer
                            </span>
                          </div>

                          <h3 className="text-[#000000] font-semibold text-lg mt-1 line-clamp-1">
                            {c.name || "-"}
                          </h3>
                        </div>

                        <div className="flex items-center">
                          <ToggleButton
                            checked={
                              c.status === "ACTIVE" || c.status === "active"
                            }
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        {/* Email */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">Email:</span>
                          <span
                            className="text-[#212121] text-sm font-medium truncate max-w-[180px]"
                            title={c.email}
                          >
                            {c.email || "-"}
                          </span>
                        </div>

                        {/* Location */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">
                            Location:
                          </span>
                          <span
                            className="text-[#212121] text-sm font-medium truncate max-w-[180px]"
                            title={locationText}
                          >
                            {locationText}
                          </span>
                        </div>

                        {/* Pincode */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">
                            Pincode:
                          </span>
                          <span className="text-[#212121] text-sm font-medium">
                            {c.pincode || "-"}
                          </span>
                        </div>

                        {/* Date */}
                        <div className="flex items-center justify-between">
                          <span className="text-[#212121] text-sm">
                            Created:
                          </span>
                          <span className="text-[#212121] text-sm font-medium">
                            {formatDate(c.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 pt-2">
                        <button
                          className="flex-1 h-10 rounded-lg bg-[#FBD037]/20 hover:bg-[#FBD037]/40 text-[#000000] text-sm font-medium transition-colors"
                          onClick={() =>
                            navigate(`/view-customer-details/${c.id}`)
                          }
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  );
                }
              }}
            />
          ) : (
            <NoDataFound />
          )}
          <div className="flex justify-center items-center py-4">
            {totalPages > 1 && (
              <Pagination
                currentPage={pageNo}
                totalPages={totalPages}
                fetchData={setPageNo}
              />
            )}
          </div>
        </div>
      </div>
      {/* Modals removed and replaced by sidebars */}
    </>
  );
}
